import type { ComponentProps, ReactNode } from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { createContext, use } from 'react';

import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

interface SelectListProps extends ComponentProps<'ul'> {
  children: ReactNode | ReactNode[] | null;
  selected: string[];
  setSelected: (data: string[]) => void;
  type?: 'single' | 'multiple';
}

interface SelectListContextType {
  selected: string[];
  deselectElement: (id: string) => void;
  selectElement: (id: string) => void;
  toggleElement: (id: string) => void;
  isSelected: (id: string) => boolean;
}

export const SelectListContext = createContext<SelectListContextType | null>(null);

export function useSelectListContext() {
  const context = use(SelectListContext);
  if (!context) {
    throw new Error('SelectList components must be used within a SelectList');
  }
  return context;
}

export function SelectList(selectListProps: SelectListProps) {
  const { children, selected, setSelected, type = 'multiple', className, ...props } = selectListProps;

  const selectElement = (id: string) => {
    if (type === 'multiple') {
      if (!selected.includes(id)) {
        setSelected([...selected, id]);
      }
    } else {
      setSelected([id]);
    }
  };
  const deselectElement = (id: string) => {
    const newSelected: string[] = selected.filter((selectedId: string) => {
      return selectedId !== id;
    });
    setSelected(newSelected);
  };

  const toggleElement = (id: string) => {
    if (type === 'multiple') {
      if (selected.includes(id)) {
        deselectElement(id);
      } else {
        selectElement(id);
      }
    } else {
      setSelected([id]);
    }
  };

  const isSelected = (id: string): boolean => {
    return selected.some((selectedId: string) => {
      return selectedId === id;
    });
  };

  return (
    <SelectListContext
      value={{
        selected,
        deselectElement,
        selectElement,
        toggleElement,
        isSelected,
      }}
    >
      <ul className={cn('w-full', className)} {...props}>
        {children}
      </ul>
    </SelectListContext>
  );
}

interface SelectListElementContextType {
  id: string;
  selected: boolean;
  toggleElement: () => void;
  selectElement: () => void;
  deselectElement: () => void;
}

export const SelectListElementContext = createContext<SelectListElementContextType | null>(null);

export function useSelectListElementContext() {
  const context = use(SelectListElementContext);
  if (!context) {
    throw new Error('SelectListElement components must be used within a SelectListElement');
  }
  return context;
}

interface SelectListElementProps extends ComponentProps<'li'> {
  id: string;
}

export function SelectListElement(selectListElement: SelectListElementProps) {
  const { id, className, ...props } = selectListElement;
  const { isSelected, toggleElement: _toggleElement, selectElement: _selectElement, deselectElement: _deselectElement } = useSelectListContext();
  const selected = isSelected(id);
  const toggleElement = () => _toggleElement(id);
  const selectElement = () => _selectElement(id);
  const deselectElement = () => _deselectElement(id);

  return (
    <SelectListElementContext value={{ id, selected, toggleElement, selectElement, deselectElement }}>
      <li
        data-slot="select-list-element"
        data-selected={selected}
        data-adjacent-radius-behavior="true"
        className={cn(
          'group [&>svg]:stroke-muted-foreground hover:bg-muted/35 hover:data-[selected=true]:bg-brand-accent/75 dark:hover:data-[selected=true]:bg-brand-accent/50 data-[selected=true]:bg-brand-accent/25 dark:data-[selected=true]:bg-brand-accent/50 relative flex h-fit items-center gap-2 rounded-md p-2 transition-[color,background-color,border-radius] duration-150 [&>svg]:shrink-0',
          'has-[+ [data-adjacent-radius-behavior]:is(:hover, [data-selected=true])]:rounded-b-none',
          className
        )}
        {...props}
      />
    </SelectListElementContext>
  );
}

interface SelectListElementTriggerProps extends Omit<ComponentProps<typeof Slot>, 'children'> {
  children: (props: { selected: boolean; toggleElement: () => void; deselectElement: () => void; selectElement: () => void }) => ReactNode;
}

export function SelectListElementTrigger(selectListElementTriggerProps: SelectListElementTriggerProps) {
  const { children, ...props } = selectListElementTriggerProps;
  const { selected, toggleElement, deselectElement, selectElement } = useSelectListElementContext();

  return (
    <Slot {...props}>
      {children({
        selected,
        toggleElement,
        deselectElement,
        selectElement,
      })}
    </Slot>
  );
}

export function SelectListElementContent(selectListElementContentProps: ComponentProps<'div'>) {
  const { className, ...props } = selectListElementContentProps;
  return <div data-slot="select-list-element-content" className={cn('flex w-full flex-col gap-0.5 overflow-hidden', className)} {...props} />;
}

export function SelectListElementTitle(selectListElementTitleProps: ComponentProps<'p'>) {
  const { className, ...props } = selectListElementTitleProps;
  return <p data-slot="select-list-element-title" className={cn('text-accent-foreground truncate text-sm', className)} {...props} />;
}

export function SelectListElementDescription(selectListElementDescriptionProps: ComponentProps<'p'>) {
  const { className, ...props } = selectListElementDescriptionProps;
  return <p data-slot="select-list-element-description" className={cn('text-muted-foreground truncate text-xs', className)} {...props} />;
}

export function SelectListElementSkeleton(selectListElementSkeletonProps: ComponentProps<'li'>) {
  const { className, ...props } = selectListElementSkeletonProps;
  return (
    <li data-slot="select-list-element-skeleton" className={cn('flex h-fit items-center gap-3 rounded-sm py-2', className)} {...props}>
      <Skeleton className={'h-10 w-10 rounded-sm'} />
      <div className={'flex flex-col gap-1'}>
        <Skeleton className={'h-3 w-28 rounded-sm'} />
        <Skeleton className={'h-3 w-16 rounded-sm'} />
      </div>
    </li>
  );
}

interface SelectListSeparatorProps extends ComponentProps<'li'> {
  message?: string;
}

export function SelectListSeparator(selectListSeparatorProps: SelectListSeparatorProps) {
  const { className, message = 'Wyszukiwane', ...props } = selectListSeparatorProps;
  return (
    <li data-slot="select-list-separator" className={cn('my-2', className)} {...props}>
      <p className="text-muted-foreground text-xs">{message}</p>
      <Separator className="mt-0.5" />
    </li>
  );
}
